import React from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle, X } from 'lucide-react';
import { Button } from './Button';

export type BannerVariant = 'warning' | 'error' | 'info' | 'success';

interface BannerProps {
    variant?: BannerVariant;
    title: string;
    description?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
    onClose?: () => void;
    className?: string; // Para customizações extras
}

const variantConfig: Record<BannerVariant, {
    bg: string;
    border: string;
    text: string;
    icon: React.ElementType;
    iconColor: string
}> = {
    warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: AlertTriangle,
        iconColor: 'text-yellow-600'
    },
    error: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: XCircle,
        iconColor: 'text-red-600'
    },
    info: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: Info,
        iconColor: 'text-blue-600'
    },
    success: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-800',
        icon: CheckCircle,
        iconColor: 'text-green-600'
    }
};

/**
 * Banner Component
 * Generic notification banner for global alerts.
 */
const Banner: React.FC<BannerProps> = ({
    variant = 'info',
    title,
    description,
    action,
    onClose,
    className = ''
}) => {
    const config = variantConfig[variant];
    const Icon = config.icon;

    return (
        <div className={`relative w-full ${className}`}>
            <div className={`mx-4 sm:mx-6 lg:mx-8 my-3 rounded-lg border ${config.bg} ${config.border}`}>
                <div className="px-4 py-3 sm:px-6">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-start sm:items-center flex-1 min-w-0">
                            <span className={`flex p-1 rounded-lg ${config.bg}`}>
                                <Icon className={`h-5 w-5 ${config.iconColor}`} aria-hidden="true" />
                            </span>

                            <div className={`ml-3 font-medium truncate sm:text-sm ${config.text}`}>
                                <span className="md:hidden">{title}</span>
                                <span className="hidden md:inline">
                                    <span className="font-bold">{title}</span>
                                    {description && <span className="ml-2 font-normal opacity-90">- {description}</span>}
                                </span>
                            </div>
                        </div>

                        <div className="flex-shrink-0 flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                            {action && (
                                <Button
                                    size="sm"
                                    variant={variant === 'error' ? 'danger' : variant === 'warning' ? 'outline' : 'secondary'}
                                    onClick={action.onClick}
                                    className="w-full sm:w-auto justify-center"
                                >
                                    {action.label}
                                </Button>
                            )}

                            {onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className={`flex p-2 rounded-md hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2`}
                                >
                                    <span className="sr-only">Fechar</span>
                                    <X className={`h-5 w-5 ${config.text}`} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Banner;
